/**
 * Shared helpers for promotion-related UI (table cells, read-only views, navigation).
 * Helpers specific to the promotion form live in `PromotionForm/promotionForm.utils.ts`.
 */

import {
  PROMOTION_FOCUS_VALUES,
  URL_SEARCH_PARAM_KEY,
} from '../../constant/UrlSearchParams.constant';
import { RoutePath } from '../../routes';

/**
 * Relative path to a promotion's detail page. Relative (`../`) because every caller
 * lives under the sibling pricing route.
 */
export const buildPromotionViewPath = (promotionId: string): string =>
  `../${RoutePath.PROMOTION_VIEW.replace(':promotionId', promotionId)}`;

/**
 * Detail-page path that opens straight to the promo codes section — used by the
 * "Add code" entry points. The page reads {@link URL_SEARCH_PARAM_KEY.PROMOTION_FOCUS}
 * to collapse the details section and expand promo codes.
 */
export const buildAddPromoCodePath = (promotionId: string, companyId: string): string => {
  const params = new URLSearchParams({
    [URL_SEARCH_PARAM_KEY.COMPANY_ID]: companyId,
    [URL_SEARCH_PARAM_KEY.PROMOTION_FOCUS]: PROMOTION_FOCUS_VALUES.PROMO_CODES,
  });
  return `${buildPromotionViewPath(promotionId)}?${params.toString()}`;
};

export const toValidDate = (value?: Date | string | null): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

export const formatPromotionDate = (
  value: Date | string | null | undefined,
  locale: string = 'en',
): string => {
  const date = toValidDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

export const toLocalDateString = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
};

/**
 * Formats a `from → to` window. Returns an empty string when neither date is set.
 * Use `endFallback` to show e.g. "No end date" when only `validFrom` is set.
 */
export const formatPromotionWindow = (
  validFrom: Date | string | null | undefined,
  validUntil: Date | string | null | undefined,
  options: { locale?: string; separator?: string; endFallback?: string } = {},
): string => {
  const { locale = 'en', separator = '→', endFallback = '' } = options;
  const from = toValidDate(validFrom);
  const to = toValidDate(validUntil);
  if (!from && !to) return '';
  const fromText = from ? formatPromotionDate(from, locale) : '';
  const toText = to ? formatPromotionDate(to, locale) : endFallback;
  return `${fromText} ${separator} ${toText}`.trim();
};
